// frontend/src/pages/EWP.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, SelectInput } from "../ui/UIComponents";

// -------------------------------
// Helpers
// -------------------------------
const mmToIn = (mm) => (Number.isFinite(Number(mm)) ? Number(mm) / 25.4 : NaN);
const fmtIn  = (v) => (Number.isFinite(v) ? v.toFixed(2) : "—");
const fmt2   = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(2) : "0.00");
const areaSqm = (w, h) => ((Number(w) || 0) / 1000) * ((Number(h) || 0) / 1000);

// -------------------------------
// Component
// -------------------------------
export default function EWP() {
  // ---- Auth (unchanged) ----
  const [pw, setPw] = useState("");
  const [ok, setOk] = useState(false);
  const [showPwError, setShowPwError] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const pwRef = useRef(null);

  // -------------------------------
  // Auth check (EWP 30-day gate)
  // -------------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/auth?action=me", { credentials: "include" });
        const j = await r.json().catch(() => ({}));
        if (!alive) return;
        setOk(Boolean(j && j.ok));
      } catch (e) {if (alive) setOk(false);
      } finally { if (alive) setCheckingAuth(false); }
    })();

    return () => {
      alive = false;
    };
  }, []);

  async function submitPw(e) {
    e?.preventDefault?.();
    setShowPwError(false);
    try {
      const r = await fetch("/api/auth?action=login", {
        method: "POST",
        headers: { "Content-Type":"application/json" },
        credentials: "include",
        body: JSON.stringify({ password: pw })
      });
      const j = await r.json().catch(() => ({}));
      if (j.ok) { setOk(true); setPw(""); } else setShowPwError(true);
    }catch{ setShowPwError(true); }
  }
  async function lock(){
    try{ await fetch("/api/auth?action=logout", { credentials: "include" }); }catch{}
    setOk(false); setPw(""); setShowPwError(false);
  }

  // ---- Data loaders ----
  const [groups, setGroups] = useState({ TOP_GROUPS: [], SUB_GROUPS: {}, PRICE_GROUP_ID: {} });
  const [groupsError, setGroupsError] = useState("");
  const [groupsLoading, setGroupsLoading] = useState(true);

  const [cfg, setCfg] = useState({ pelmetTable: [], groups: {}, evb: { groupIds: [], options: [], surcharges: [], title: "EVB" } });
  const [cfgError, setCfgError] = useState("");
  const [cfgLoading, setCfgLoading] = useState(true);

  // NEW: template state
  const [tpl, setTpl] = useState(null);
  const [textError, setTextError] = useState(null);
  const [textLoading, setTextLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/ewp_group.json?v=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`Failed to load ewp_group.json (${r.status})`);
        const j = await r.json();
        if (!alive) return;
        setGroups({
          TOP_GROUPS: Array.isArray(j.TOP_GROUPS) ? j.TOP_GROUPS : [],
          SUB_GROUPS: j.SUB_GROUPS && typeof j.SUB_GROUPS === "object" ? j.SUB_GROUPS : {},
          PRICE_GROUP_ID: j.PRICE_GROUP_ID && typeof j.PRICE_GROUP_ID === "object" ? j.PRICE_GROUP_ID : {},
        });
        setGroupsError("");
      } catch (e) {
        if (alive) setGroupsError(e?.message || "Unable to load ewp_group.json");
      } finally {
        if (alive) setGroupsLoading(false);
      }
    })();

    (async () => {
      try {
        const r = await fetch(`/ewp_config.json?v=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`Failed to load ewp_config.json (${r.status})`);
        const j = await r.json();
        if (!alive) return;
        setCfg(j);
        setCfgError("");
      } catch (e) {
        if (alive) setCfgError(e?.message || "Unable to load ewp_config.json");
      } finally {
        if (alive) setCfgLoading(false);
      }
    })();

    (async () => {
      try {
        const r = await fetch(`/ewp_text.json?v=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`Failed to load ewp_text.json (${r.status})`);
        const j = await r.json();
        if (!alive) return;
        setTpl(j);
        setTextError("");
      } catch (e) {
        if (alive) setTextError(e?.message || "Unable to load ewp_text.json");
      } finally {
        if (alive) setTextLoading(false);
      }
    })();

    return () => { alive = false; };
  }, []);

  // ---- Selections & dims ----
  const [top, setTop] = useState(null);
  const [sub, setSub] = useState(null);
  const [subSearch, setSubSearch] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");

  const widthIn  = useMemo(() => mmToIn(width), [width]);
  const heightIn = useMemo(() => mmToIn(height), [height]);
  const sqm = useMemo(() => areaSqm(width, height), [width, height]);

  const widthRef  = useRef(null);
  const heightRef = useRef(null);
  useEffect(() => { setSubSearch(""); }, [top]);

  // ---- Options (per group id) ----
  const gid = top?.key && sub?.key ? (groups.PRICE_GROUP_ID?.[top.key] || {})[sub.key] : null;
  const [optsByGroup, setOptsByGroup] = useState({});

  function defaultOptionsFor(currentCfg, groupId) {
    if ((currentCfg?.evb?.groupIds || []).includes(groupId)) {
      const o = {};
      for (const opt of currentCfg.evb.options || []) o[opt.key] = opt.default;
      return o;
    }
    const g = currentCfg.groups?.[groupId];
    if (!g) return {};
    const o = {};
    for (const opt of g.options || []) o[opt.key] = opt.default;
    return o;
  }

  useEffect(() => {
    if (!gid || cfgLoading) return;
    setOptsByGroup((prev) => (prev[gid] ? prev : { ...prev, [gid]: defaultOptionsFor(cfg, gid) }));
  }, [gid, cfg, cfgLoading]);

  // currentOpts now memoized to satisfy react-hooks/exhaustive-deps
  const currentOpts = useMemo(
    () => (gid ? (optsByGroup[gid] || {}) : {}),
    [gid, optsByGroup]
  );
  const setOpt = (key, value) =>
    setOptsByGroup((prev) => ({
      ...prev,
      [gid]: { ...(prev[gid] || {}), [key]: value },
    }));

  function resetAllOptionsAndPrice() {
    setOptsByGroup((prev) => (gid ? { ...prev, [gid]: defaultOptionsFor(cfg, gid) } : prev));
    setPriceBase(null); setPriceErr(""); setPriceDetail(""); setModalOpen(false); setPriceMeta(null);
  }
  function clickTop(g){ setTop(g); setSub(null); resetAllOptionsAndPrice(); }
  function clickSub(s){ setSub(s); resetAllOptionsAndPrice(); setTimeout(()=>widthRef.current?.focus(),0); }

  // ---- Filter sub-groups ----
  const filteredSubs = useMemo(() => {
    const list = top ? (groups.SUB_GROUPS?.[top.key] || []) : [];
    const q = subSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter(s =>
      (s.vi || "").toLowerCase().includes(q) ||
      (s.en || "").toLowerCase().includes(q) ||
      (s.key || "").toLowerCase().includes(q)
    );
  }, [top, subSearch, groups.SUB_GROUPS]);

  // ---- Pricing ----
  const [loading, setLoading] = useState(false);
  const [priceErr, setPriceErr] = useState("");
  const [priceDetail, setPriceDetail] = useState("");
  const [priceBase, setPriceBase] = useState(null);
  const [priceMeta, setPriceMeta] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");

  const adjustments = useMemo(() => {
    if (!gid) return [];
    const list = [];

    // helpers
    const pelmetPriceForWidth = (table, wmm) => {
      const w = Number(wmm) || 0;
      for (const [limit, price] of (table || [])) if (w <= limit) return { limit, price };
      const [limit, price] = (table || [])[ (table || []).length - 1 ] || [0,0];
      return { limit, price };
    };
    const addOptionPricing = (optDef) => {
      const choice = currentOpts[optDef.key];
      let delta = 0;
      for (const rule of optDef.pricing || []) {
        const cond = rule.when || {};
        if (cond.eq != null && cond.eq !== choice) continue;
        if (rule.delta != null) delta += Number(rule.delta) || 0;
        if (rule.per === "sqm")       delta += (Number(rule.rate) || 0) * (Number(sqm) || 0);
        if (rule.per === "height_m")  delta += (Number(rule.rate) || 0) * ((Number(height) || 0) / 1000);
        if (rule.per === "pelmet_table") {
          const { limit, price } = pelmetPriceForWidth(cfg.pelmetTable || [], width);
          delta += price || 0;
          list.push({ label: `Pelmet (≤ ${limit} mm)`, choice, delta });
          return;
        }
      }
      list.push({ label: optDef.label, choice, delta });
    };

    const isEVB = gid && (cfg.evb?.groupIds || []).includes(gid);
    if (isEVB) {
      for (const opt of cfg.evb.options || []) addOptionPricing(opt);
      for (const s of cfg.evb.surcharges || []) {
        const cond = s.when || {};
        if (cond.width_gt != null && !((Number(width) || 0) > Number(cond.width_gt))) continue;
        list.push({ label: s.label || "Surcharge", choice: s.choice || "Auto", delta: Number(s.delta) || 0 });
      }
      return list;
    }

    const g = cfg.groups?.[gid];
    if (!g) return list;
    for (const opt of g.options || []) addOptionPricing(opt);
    return list;
  }, [cfg, gid, currentOpts, width, height, sqm]);

  const totalPrice = useMemo(() => {
    const base = Number(priceBase) || 0;
    const adj = adjustments.reduce((s, a) => s + Number(a.delta || 0), 0);
    return base + adj;
  }, [priceBase, adjustments]);

  async function fetchPrice(){
    try{
      setLoading(true); setPriceErr(""); setPriceDetail(""); setPriceBase(null); setPriceMeta(null);
      setModalTitle("Đang tra giá… / Fetching price…"); setModalOpen(true);
      const groupId = gid;
      if (!groupId) throw new Error("Không tìm thấy nhóm giá / Missing price group");
      const params = new URLSearchParams({ w: width, h: height, group: String(groupId) });
      const r = await fetch(`/api/ewp_price?${params.toString()}`, { credentials: "include" });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        const t = await r.text();
        throw new Error(t.slice(0, 200));
      }
      const data = await r.json();
      if (data.error) { setPriceErr(data.error); setPriceDetail(data.detail || ""); setModalTitle("Lỗi / Error"); return; }
      setPriceBase(data.exw ?? null);
      setPriceMeta(data.rb ? data : null);
      setModalTitle("Kết quả giá / Price result");
    }catch(e){
      setPriceErr(e.message || "Unexpected error");
      setModalTitle("Lỗi / Error");
    }finally{
      setLoading(false);
    }
  }
  function onSubmitDims(e){ e.preventDefault(); if(!top||!sub||!width||!height) return; fetchPrice(); }

  // ---- Download (delegated to /public/ewp_export.js) ----
  function downloadTxt(){
    try{
      const now = new Date();
      const ts = now.toISOString().slice(0,19).replace(/[:T]/g,"-");
      const map = {
        timestamp: now.toLocaleString(),
        ts,
        top_vi: top?.vi || "",
        top_en: top?.en || "",
        top_key: top?.key || "",
        sub_vi: sub?.vi || "",
        sub_en: sub?.en || "",
        sub_key: sub?.key || "",
        groupId: String(gid || ""),
        width: String(width || ""),
        height: String(height || ""),
        width_in: (Number(width)/25.4).toFixed(2),
        height_in: (Number(height)/25.4).toFixed(2),
        area_sqm: ((Number(width)||0)/1000 * ((Number(height)||0)/1000)).toFixed(2),
        base_exw: (Number(priceBase)||0).toFixed(2),
        total_exw: (Number(totalPrice)||0).toFixed(2),
        // RB extras if present
        roll_price: priceMeta?.roll_price != null ? (Number(priceMeta.roll_price)||0).toFixed(2) : "",
        cut_price:  priceMeta?.cut_price  != null ? (Number(priceMeta.cut_price)||0).toFixed(2) : "",
        suggestion: priceMeta?.suggestion || "",
        working_steps: Array.isArray(priceMeta?.working) ? priceMeta.working : []
      };
      if (!tpl) throw new Error(textError || "Template not loaded");
      if (!window.EWP_EXPORT?.downloadTxt) throw new Error("Export helper not loaded");
      window.EWP_EXPORT.downloadTxt(map, tpl, adjustments);
    }catch(e){
      alert("Không thể tải xuống / Unable to download: " + (e?.message || e));
    }
  }

  // -------------------------------
  // Render
  // -------------------------------
  return (
    <div className="app-page">
      {!ok ? (
        <main className="container">
          <h2 style={{marginBottom:12}}>Xin giá SP / Request Price</h2>
          <div className="box" style={{maxWidth:420}}>
            {checkingAuth ? <p style={{marginTop:0}}>Đang kiểm tra quyền truy cập… / Checking access…</p> : (
              <>
                <p style={{marginTop:0}}>Vui lòng nhập mật khẩu để tiếp tục / Enter password to continue.</p>
                <form onSubmit={submitPw} style={{display:"grid",gap:10}}>
                  <label className="label">Mật khẩu / Password</label>
                  <input ref={pwRef} className="text-input" type="password" value={pw} onChange={(e)=>setPw(e.target.value)} placeholder="••••••••" autoFocus />
                  {showPwError && <div style={{color:"crimson"}}>Sai mật khẩu / Wrong password</div>}
                  <div className="actions"><Button type="submit">Xác nhận / Confirm</Button></div>
                </form>
              </>
            )}
          </div>
        </main>
      ) : (
        <main className="container">
          <h2 style={{marginBottom:12}}>Xin giá SP / Request Price</h2>

          {(groupsError || cfgError || textError) && (
            <div className="box" style={{marginBottom:16,color:"crimson"}}>
              {groupsError && <>Lỗi nạp nhóm / Failed to load groups: {groupsError}<br/></>}
              {cfgError && <>Lỗi nạp cấu hình / Failed to load config: {cfgError}<br/></>}
              {textError && <>Lỗi nạp mẫu tải xuống / Failed to load text template: {textError}</>}
            </div>
          )}

          {/* 1) Category */}
          <div className="box" style={{marginBottom:16}}>
            <div style={{marginBottom:8,fontWeight:600}}>1) Nhóm sản phẩm / Category</div>
            <div className="menu-grid" style={{marginBottom:8}}>
              {groupsLoading ? (
                <div style={{opacity:.7}}>Đang nạp danh mục… / Loading categories…</div>
              ) : (groups.TOP_GROUPS||[]).length===0 ? (
                <div style={{opacity:.7}}>Không có dữ liệu nhóm / No categories</div>
              ) : (
                (groups.TOP_GROUPS||[]).map((g)=>(
                  <button key={g.id} className="menu-card" onClick={()=>clickTop(g)}
                          style={top?.key===g.key?{borderColor:"#000",boxShadow:"0 0 0 2px #00000010"}:undefined}>
                    <span className="menu-id">{g.id}.</span><span className="menu-text">{g.vi} / {g.en}</span>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* 2) Sub-group */}
          <div className="box" style={{marginBottom:16}}>
            <div style={{marginBottom:8,fontWeight:600}}>2) Dòng sản phẩm / Sub-group {top ? "— "+top.vi : ""}</div>
            {top && (
              <div className="field" style={{marginBottom:8}}>
                <input className="text-input" value={subSearch} onChange={(e)=>setSubSearch(e.target.value)}
                       placeholder='Gõ để lọc / Type to filter "sub-group"' />
              </div>
            )}
            <div className="menu-grid" style={{marginBottom:8}}>
              {(!top || (groups.SUB_GROUPS?.[top.key]||[]).length===0) ? (
                <div style={{opacity:.7}}>{top ? "Không có dữ liệu / No sub-groups" : "Chọn nhóm trước / Choose a category first"}</div>
              ) : filteredSubs.length===0 ? (
                <div style={{opacity:.7}}>Không có kết quả / No matches</div>
              ) : (
                filteredSubs.map((s)=>(
                  <button key={s.id} className="menu-card" onClick={()=>clickSub(s)}
                          style={sub?.key===s.key?{borderColor:"#000",boxShadow:"0 0 0 2px #00000010"}:undefined}>
                    <span className="menu-id">{s.id}.</span><span className="menu-text">{s.vi} / {s.en}</span>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* 3) Dimensions */}
          <form className="box" onSubmit={onSubmitDims}>
            <div style={{marginBottom:8,fontWeight:600}}>3) Kích thước / Dimensions</div>
            <div className="field">
              <label className="label">Chiều rộng / Width (mm)</label>
              <div className="control-row">
                <input ref={widthRef} className="text-input" inputMode="decimal" value={width}
                       onChange={(e)=>setWidth(e.target.value.replace(/[^\d.]/g,""))}
                       placeholder="e.g. 2200" disabled={!sub} required />
                <span className="unit">≈ <strong>{fmtIn(widthIn)}</strong> in</span>
              </div>
            </div>
            <div className="field">
              <label className="label">Chiều cao / Height (mm)</label>
              <div className="control-row">
                <input ref={heightRef} className="text-input" inputMode="decimal" value={height}
                       onChange={(e)=>setHeight(e.target.value.replace(/[^\d.]/g,""))}
                       placeholder="e.g. 2600" disabled={!sub} required />
                <span className="unit">≈ <strong>{fmtIn(heightIn)}</strong> in</span>
              </div>
            </div>
            <div className="actions" style={{marginTop:6}}>
              <Button type="submit" disabled={!top||!sub||!width||!height||loading||cfgLoading||textLoading}>
                {loading ? "Đang tra giá… / Fetching…" : "Tiếp tục / Continue"}
              </Button>
              <Button onClick={lock} type="button" style={{marginLeft:8}}>Khoá lại / Lock</Button>
            </div>
          </form>

          {/* Modal (now global) */}
          <Modal
            open={modalOpen}
            title={modalTitle}
            onClose={()=>{ setModalOpen(false); }}
          >
            <div style={{fontSize:12,opacity:.7,marginBottom:8}}>
              Debug → groupId: <code>{String(gid || "")}</code>,{" "}
              w: <code>{width}</code>, h: <code>{height}</code>, sqmt: <code>{fmt2(sqm)}</code>
            </div>

            {loading && <div>Vui lòng đợi… / Please wait…</div>}

            {!loading && priceErr && (
              <div style={{color:"crimson",whiteSpace:"pre-wrap"}}>
                Lỗi / Error: {priceErr}{priceDetail ? "\nChi tiết / Detail: "+priceDetail : ""}
              </div>
            )}

            {!loading && !priceErr && (
              <div style={{display:"grid",gap:12}}>
                {/* RB Fabric A/B comparison (if RB meta present) */}
                {priceMeta?.rb && (() => {
                  const cutH = Number(priceMeta?.cut_h || 30000);
                  const H = Number(height) || 0;
                  const fullRolls = Math.floor(H / cutH);
                  const remainder = H % cutH;

                  const rollPrice = Number(priceMeta?.roll_price) || 0;
                  const cutPrice = Number(priceMeta?.cut_price) || 0;

                  const totalA = (rollPrice * fullRolls) + (remainder > 0 ? cutPrice : 0);
                  const totalB = rollPrice * (fullRolls + (remainder > 0 ? 1 : 0));

                  const chosen = remainder > 0 && totalB < totalA ? "B" : "A";

                  return (
                    <div className="box" style={{margin:0}}>
                      <div style={{fontWeight:600,marginBottom:6}}>RB Fabric — Roll/Cut</div>
                      <div>Roll Price = <strong>{fmt2(rollPrice)}</strong> + Cut Price = <strong>{fmt2(cutPrice)}</strong></div>
                      <div style={{marginTop:6}}>
                        <div><strong>Strategy A</strong>: {fullRolls} roll(s){remainder>0 ? " + cut remainder" : ""} → <strong>{fmt2(totalA)}</strong></div>
                        {remainder>0 && (
                          <div><strong>Strategy B</strong>: {fullRolls + 1} roll(s) → <strong>{fmt2(totalB)}</strong></div>
                        )}
                        <div style={{marginTop:4}}>Chosen: <strong>{chosen}</strong> — {priceMeta?.suggestion}</div>
                      </div>
                      <details style={{marginTop:6}}>
                        <summary>Working / Chi tiết tính</summary>
                        <ul style={{margin:0,paddingLeft:18}}>
                          {(priceMeta.working||[]).map((w,i)=><li key={i}>{w}</li>)}
                        </ul>
                      </details>
                    </div>
                  );
                })()}

                {/* Options / Summary */}
                {(() => {
                  const isEVB = gid && (cfg.evb?.groupIds || []).includes(gid);
                  const groupCfg = gid ? (isEVB ? cfg.evb : cfg.groups?.[gid]) : null;
                  const groupTitle = isEVB ? (cfg.evb?.title || "EVB") : (groupCfg?.title || (sub?.en || sub?.vi || "Options"));

                  return (
                    <>
                      {groupCfg && (
                        <div className="box" style={{margin:0}}>
                          <div style={{fontWeight:600,marginBottom:6}}>Tuỳ chọn / Options — {groupTitle}</div>
                          {(groupCfg.options || []).map((opt) => (
                            <div key={opt.key} className="field">
                              <label className="label">{opt.label}</label>
                              <div className="control-row">
                                <SelectInput
                                  value={currentOpts[opt.key] ?? opt.default}
                                  onChange={(e) => setOpt(opt.key, e.target.value)}
                                  choices={opt.choices || []}
                                />
                                <span className="unit">
                                  Δ {fmt2((adjustments.find(a => a.label===opt.label || a.label.startsWith(opt.label))||{}).delta||0)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="box" style={{margin:0}}>
                        <div style={{fontWeight:600,marginBottom:6}}>Tổng hợp / Summary</div>
                        <div>Giá cơ sở / Base EXW: <strong>{fmt2(priceBase)}</strong></div>
                        {adjustments.map((a,i)=>{
                          const sign = Number(a.delta||0)>=0?"+":"-";
                          const abs = Math.abs(Number(a.delta||0));
                          return <div key={i}>{a.label}: <em>{a.choice}</em> ({sign}{fmt2(abs)})</div>;
                        })}
                        <div style={{borderTop:"1px dashed #ddd",marginTop:8,paddingTop:8}}>
                          Giá EXW / EXW price: <strong>{fmt2(totalPrice)}</strong>
                        </div>
                        <div className="actions" style={{marginTop:10}}>
                          <Button type="button" onClick={downloadTxt} disabled={priceBase==null || !tpl}>
                            Tải xuống / Download (.txt)
                          </Button>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </Modal>
        </main>
      )}
    </div>
  );
}
